import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, CheckCircle2, XCircle, Building2, Users } from 'lucide-react';
import Logo from '../components/Logo';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [inviteValid, setInviteValid] = useState(null);
  const [condoInfo, setCondoInfo] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [successPending, setSuccessPending] = useState(false);

  // Entry points:
  // 1. ?condo=<id>   - Single condo QR (resident self-registration, needs admin approval)
  // 2. ?ref=<code>   - Family referral link (joins an existing household)
  // 3. ?code=<code>  - (Legacy) Invite code per-resident (still supported)
  const condoParam = searchParams.get('condo');
  const refParam = searchParams.get('ref');
  const inviteParam = searchParams.get('code');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    invite_code: inviteParam || '',
    referral_code: refParam || '',
    condominium_id: condoParam || '',
  });

  // Fetch condominium info if we arrived via the condo QR
  useEffect(() => {
    const loadCondo = async () => {
      if (!condoParam) return;
      try {
        const res = await api.get(`/public/condominiums/${condoParam}`);
        setCondoInfo({ condominium_name: res.data.name, address: res.data.address, via_condo: true });
      } catch (e) {
        setCondoInfo(null);
        toast.error('Condominio non trovato');
      }
    };
    loadCondo();
  }, [condoParam]);

  // Keep legacy invite-code flow working
  useEffect(() => {
    if (condoParam) return; // skip if condo flow active
    if (formData.invite_code.length >= 8) {
      validateInviteCode(formData.invite_code);
    } else {
      setInviteValid(null);
      if (!condoParam) setCondoInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.invite_code, condoParam]);

  const validateInviteCode = async (code) => {
    try {
      const response = await api.get(`/invite-codes/validate/${code}`);
      setInviteValid(true);
      setCondoInfo({ condominium_name: response.data.condominium_name, via_invite: true });
    } catch (error) {
      setInviteValid(false);
      setCondoInfo(null);
    }
  };

  useEffect(() => {
    if (refParam) {
      setReferralInfo({ code: refParam });
    }
  }, [refParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    // Require at least one valid entry point
    if (!condoParam && !refParam && !inviteValid) {
      toast.error('Inserisci un codice invito valido o accedi dal link del condominio/famiglia');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
      };
      if (condoParam) payload.condominium_id = condoParam;
      if (refParam) payload.referral_code = refParam;
      if (formData.invite_code && !condoParam) payload.invite_code = formData.invite_code;

      const res = await register(payload);
      if (res?.user?.approval_status === 'pending') {
        setSuccessPending(true);
      } else {
        toast.success('Registrazione completata con successo!');
        navigate('/dashboard/user');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (successPending) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl">Richiesta inviata!</CardTitle>
            <CardDescription>
              Il tuo account è stato creato ma deve essere approvato dall'amministratore del condominio.
              Riceverai una conferma appena sarà attivo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              Vai al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-200 rounded-full opacity-20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-slate-300 rounded-full opacity-30 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla home
          </Link>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="space-y-1 text-center pb-6">
            <div className="mx-auto mb-4 flex items-center justify-center">
              <Logo linkTo={null} size="lg" />
            </div>
            <CardTitle className="text-2xl font-serif">Registrati su AIAC Card</CardTitle>
            <CardDescription>
              {condoParam
                ? 'Completa la registrazione come residente del condominio.'
                : refParam
                ? 'Sei stato invitato a unirti a una famiglia AIAC.'
                : 'Crea il tuo account per accedere alle proposte esclusive'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Info banner based on entry point */}
            {condoInfo && (
              <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-3">
                <Building2 className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">{condoInfo.condominium_name}</p>
                  {condoInfo.address && <p className="text-xs text-emerald-700">{condoInfo.address}</p>}
                </div>
              </div>
            )}
            {referralInfo && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3">
                <Users className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Invito Famiglia</p>
                  <p className="text-xs text-amber-700 font-mono">Codice: {referralInfo.code}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Show invite code field only if no condo/referral param */}
              {!condoParam && !refParam && (
                <div className="space-y-2">
                  <Label htmlFor="invite_code">Codice Invito *</Label>
                  <div className="relative">
                    <Input
                      id="invite_code"
                      type="text"
                      placeholder="AIAC-XXXXXX"
                      value={formData.invite_code}
                      onChange={(e) => setFormData({ ...formData, invite_code: e.target.value.toUpperCase() })}
                      required
                      className={inviteValid === true ? 'border-emerald-500 pr-10' : inviteValid === false ? 'border-red-500 pr-10' : ''}
                      data-testid="register-invite-code-input"
                    />
                    {inviteValid === true && (
                      <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                    )}
                    {inviteValid === false && (
                      <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                    )}
                  </div>
                  {inviteValid === false && (
                    <p className="text-sm text-red-500">Codice non valido o scaduto</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo *</Label>
                <Input id="name" type="text" placeholder="Mario Rossi" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required data-testid="register-name-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" placeholder="nome@esempio.it" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required data-testid="register-email-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input id="password" type="password" placeholder="••••••••" value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required minLength={6} data-testid="register-password-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password *</Label>
                <Input id="confirmPassword" type="password" placeholder="••••••••" value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required data-testid="register-confirm-password-input" />
              </div>

              <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600"
                disabled={loading || (!condoParam && !refParam && !inviteValid)}
                data-testid="register-submit-btn">
                {loading ? 'Registrazione in corso...' : 'Crea Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              Hai già un account?{' '}
              <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium">Accedi</Link>
            </div>
            <div className="mt-2 text-center text-xs text-slate-500">
              Sei un esercente?{' '}
              <Link to="/register-company" className="text-amber-600 hover:text-amber-700 font-medium">
                Registra la tua attività
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
